import { Injectable } from '@angular/core';
import { TicketPaxQuantities } from 'src/entities/ticket';
import { isSsffCatalogBlockedOnChild } from 'src/entities/file';

/** Valores de Core `estadoServicioTickets`. */
export type EstadoServicioTicketsCore =
  | 'CREADO'
  | 'EN_PROCESO'
  | 'RESERVADO'
  | 'NUEVA_ADICION';

const ESTADO_SERVICIO_TICKETS_LABEL: Record<
  EstadoServicioTicketsCore,
  string
> = {
  CREADO: 'Creado',
  EN_PROCESO: 'En Proceso',
  RESERVADO: 'Reservado',
  NUEVA_ADICION: 'Nueva Adición'
};

@Injectable({
  providedIn: 'root'
})
export class ServicioFormatterService {
  /**
   * Adapta un item de getTicketeriaServicesPage al modelo de fila de Ticketería.
   * Keys de acción: serviceId → id; idItinerary se preserva.
   */
  formatServicio(data: any): Record<string, any> {
    return this.buildRowFromSummary(data);
  }

  formatServiciotoCompare(data: any): Record<string, any> {
    // Validación OS aún puede recibir shape Sales `{ service, tickets }`.
    const summary =
      data?.service != null ? this.legacyNestedServiceToSummary(data) : data;
    const row = this.formatServicio(summary);
    return {
      ...row,
      selected: true,
      tickets: (data.tickets ?? row['tickets'] ?? []).map((item: any) => ({
        ...item,
        checked: true
      }))
    };
  }

  /** Solo para compare/OS: adapta servicio Sales anidado al shape summary. */
  private legacyNestedServiceToSummary(data: any): Record<string, any> {
    const service = data.service ?? {};
    const itinerary = data.itinerary ?? {};
    const provider =
      service.providerSnapshot ?? service.objProvider ?? null;

    return {
      serviceId: service.id ,
      idItinerary: service.idItinerary,
      categoriaServicio: service.categoriaServicio,
      file: {
        fileNumber: itinerary.fileNumber,
        fileExtend: itinerary.fileExtend,
        displayState: itinerary.estadoFile
      },
      startDateService: service.startDateService ?? null,
      providerSnapshot: provider
        ? {
            id: provider.id ?? service.providerId,
            nombre: provider.nombre,
            razonSocial: provider.razonSocial ?? null,
            correoReserva: provider.correoReserva,
            estadoProveedor: provider.estadoProveedor ?? null,
            tipoProveedor: provider.tipoProveedor ?? null,
            condicionesPagos: provider.condicionesPagos ?? null
          }
        : null,
      paxQuantity: service.paxQuantity,
      tcQuantity: service.tcQuantity,
      nombreServicio: service.nombreServicio,
      circuitoTren: service.circuitoTren,
      varianteFrecuencia: service.varianteFrecuencia,
      horario: service.horario,
      ticketCount: data.ticketCount,
      paxTickets: service.paxTickets,
      total: this.handleCosto(service),
      moneda: service.moneda,
      estadoServicioTickets: service.estadoServicioTickets,
      order: service.order,
      tickets: data.tickets,
      hasInvalidFechaOperacion: service.hasInvalidFechaOperacion,
      ssffproduct: service.ssffproduct,
      serviceAdd: Boolean(service.serviceAdd),
      productId: service.productId,
      isSSFFPadre: itinerary.isSSFFPadre
    };
  }

  private buildRowFromSummary(data: any): Record<string, any> {
    return {
      // Keys de acción / UI local
      id: data.serviceId,
      selected: false,
      order: data.order ?? null,
      tickets: [],
      ticketsLoaded: false,
      // Campos Core (paint-ready)
      serviceId: data.serviceId,
      idItinerary: data.idItinerary,
      startDateService: data.startDateService ?? null,
      ssffproduct: Boolean(data.ssffproduct),
      serviceAdd: Boolean(data.serviceAdd),
      productId: data.productId ?? null,
      file: data.file ?? null,
      isSSFFPadre: Boolean(
        data.isSSFFPadre ?? data.file?.isSSFFPadre ?? data.file?.isSsffPadre
      ),
      categoriaServicio: data.categoriaServicio,
      nombreServicio: data.nombreServicio,
      circuitoTren: data.circuitoTren,
      varianteFrecuencia: data.varianteFrecuencia,
      horario: this.formatHorario(data.horario),
      paxQuantity: data.paxQuantity,
      tcQuantity: data.tcQuantity,
      ticketCount: data.ticketCount ?? 0,
      paxTickets: data.paxTickets ?? null,
      // Detalle UI de tickets (derivado solo para pintar tipos)
      ticket: data.paxTickets ? this.handleTipoTicket(data.paxTickets) : [],
      providerSnapshot: data.providerSnapshot ?? null,
      total: data.total ?? 0,
      moneda: this.normalizeMoneda(data.moneda),
      tipoCambio: data.tipoCambio ?? null,
      tipoMoneda: data.tipoMoneda ?? data.moneda ?? null,
      estadoServicioTickets: data.estadoServicioTickets ?? null,
      hasInvalidFechaOperacion: false
    };
  }

  handleTipoTicket(paxTickets: TicketPaxQuantities): Array<{
    nombreDB: string;
    tipo: string;
    cantidad: number;
  }> {
    const baseTickets = [
      { nombreDB: 'ticketAdulto', tipo: ' Ticket Adulto', cantidad: 0 },
      { nombreDB: 'ticketEstudiante', tipo: ' Ticket Estudiante', cantidad: 0 },
      { nombreDB: 'ticketNacional', tipo: ' Ticket Nacional', cantidad: 0 },
      { nombreDB: 'ticketNino', tipo: ' Ticket Niño', cantidad: 0 }
    ];
    for (const key in paxTickets) {
      const findBaseTicketIndex = baseTickets.findIndex(
        (b) => b.nombreDB === key
      );
      if (findBaseTicketIndex !== -1) {
        const cantidad = Number(
          (paxTickets as Record<string, any>)[key] ?? 0
        );
        baseTickets[findBaseTicketIndex].cantidad = Number.isFinite(cantidad)
          ? cantidad
          : 0;
      }
    }
    return baseTickets.filter((b) => b.cantidad > 0);
  }

  /** Total de fila summary o ticket expandido (legacy solo en expand). */
  handleCosto(data: any): number {
    if (data?.total != null) {
      return Number(data.total) || 0;
    }
    const moneda = data?.objTarifa?.monedaTarifa ?? data.moneda;
    if (moneda === 'DOLAR' || moneda === '$' || moneda === 'D') {
      return !data.editOperation
        ? data.totalCostoDolares
        : data.newTotalCostoDolares;
    }
    return !data.editOperation
      ? data.totalCostoSoles
      : data.newTotalCostoSoles;
  }

  /** Core envía "D" | "S"; UI muestra $ / S/. */
  normalizeMoneda(moneda: string | null | undefined): string {
    if (!moneda) return 'S/';
    const value = moneda.toUpperCase();
    if (
      value === 'D' ||
      value === 'DOLAR' ||
      value === 'USD' ||
      value === '$'
    ) {
      return '$';
    }
    if (
      value === 'S' ||
      value === 'S/' ||
      value === 'SOLES' ||
      value === 'PEN'
    ) {
      return 'S/';
    }
    return moneda;
  }

  formatHorario(horario: string | null | undefined): string | null {
    if (!horario) return null;
    if (/^\d{1,2}:\d{2}\s?(AM|PM)$/i.test(horario.trim())) {
      return horario;
    }
    if (
      /^\d{4}-\d{2}-\d{2}T/.test(horario) ||
      !Number.isNaN(Date.parse(horario))
    ) {
      return this.convertirHora12Horas(horario);
    }
    return horario;
  }

  mapEstadoServicioTicketsLabel(
    estadoServicioTickets: string | null | undefined
  ): string {
    if (!estadoServicioTickets) return '-';
    const key = estadoServicioTickets.toUpperCase() as EstadoServicioTicketsCore;
    return ESTADO_SERVICIO_TICKETS_LABEL[key] ?? estadoServicioTickets;
  }

  /**
   * Recalcula estadoServicioTickets tras mutaciones locales en expand.
   * Prioriza estados de tickets (Core); order del padre es fallback.
   */
  handleEstadoServicio(element: any): string {
    const estadoTickets = element.tickets?.map(
      (item: any) => item.estadoTicket
    );

    if (!estadoTickets?.length) {
      return element.estadoServicioTickets ?? 'CREADO';
    }

    const normalize = (estado: string) => {
      const key = String(estado ?? '')
        .toUpperCase()
        .replace(/\s+/g, '_');
      if (key === 'CREATED') return 'CREADO';
      if (key === 'ENPROCESO') return 'EN_PROCESO';
      return key;
    };
    const normalized = estadoTickets.map(normalize);

    const ticketConfirmados = normalized.every(
      (estado: string) => estado === 'CONFIRMADO'
    );

    const hasTicketOrder = element.tickets?.some(
      (t: any) => t.order?.orderNumber || t.order?.orderId
    );
    const orderNumber =
      element.service?.order?.orderNumber ?? element.order?.orderNumber;

    if ((hasTicketOrder || orderNumber) && !ticketConfirmados) {
      return 'EN_PROCESO';
    }
    if (normalized.every((estado: string) => estado === 'CREADO')) {
      return 'CREADO';
    }
    if (ticketConfirmados) {
      return 'RESERVADO';
    }
    return 'EN_PROCESO';
  }

  /** ¿Hay tickets CREADO elegibles para generar OS? */
  hasCreatedTicketsForOs(row: any): boolean {
    if (row.tickets?.length) {
      return row.tickets.some((ti: any) => {
        const estado = String(ti.estadoTicket ?? '')
          .toUpperCase()
          .replace(/\s+/g, '_');
        const normalized = estado === 'CREATED' ? 'CREADO' : estado;
        const total =
          ti.total != null ? Number(ti.total) : this.handleCosto(ti);
        return normalized === 'CREADO' && total > 0;
      });
    }
    const estado = String(row.estadoServicioTickets ?? '').toUpperCase();
    return (
      estado === 'CREADO' ||
      estado === 'EN_PROCESO' ||
      estado === 'NUEVA_ADICION'
    );
  }

  /**
   * Ticketería / expand: ¿el servicio puede seleccionarse para OS?
   * File padre: sí (como file normal).
   * File hijo + catálogo SSFF: no. serviceAdd / upgrade: sí.
   */
  isTicketServiceSelectableForOs(row: any, isParentFile = false): boolean {
    if (!(Number(row?.total) > 0)) return false;
    if (isSsffCatalogBlockedOnChild(row, isParentFile)) return false;
    return this.hasCreatedTicketsForOs(row);
  }

  convertirHora12Horas(fechaString: string): string {
    const fecha = new Date(fechaString);

    let horas = fecha.getHours();
    const minutos = fecha.getUTCMinutes();
    const ampm = horas >= 12 ? 'PM' : 'AM';

    horas = horas % 12 || 12;
    return `${horas}:${minutos.toString().padStart(2, '0')} ${ampm}`;
  }
}
